import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../Config/apiClient';
import { API_ENDPOINTS } from '../Config/api';
import ClipLoader from 'react-spinners/ClipLoader';
import { FiAward, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

/**
 * Componente standalone che mostra SOLO la card "Statistiche Formazione"
 * estratta dalla dashboard Awareness. Usato nella pagina CyberLog.
 */
export default function AwarenessTrainingStats({ companyId = null }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [training, setTraining] = useState(null);
  const { t } = useTranslation();

  const fetchTraining = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = companyId
        ? API_ENDPOINTS.awarenessDashboardCompany(companyId)
        : API_ENDPOINTS.awarenessDashboard;
      const response = await apiClient.get(url);
      setTraining(response.data?.training || null);
    } catch (err) {
      console.error('Error fetching awareness training stats:', err);
      setError(err.response?.data?.message || t('awarenessTrainingStats.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTraining();
  }, [companyId]);

  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6 h-full w-full flex items-center justify-center">
        <ClipLoader size={40} />
      </div>
    );
  }

  if (error || !training) {
    return (
      <div className="bg-white border rounded-lg p-6 h-full w-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <FiAlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>{error || t('awarenessTrainingStats.noData')}</p>
          <button
            onClick={fetchTraining}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto"
          >
            <FiRefreshCw className="w-4 h-4" /> {t('awarenessTrainingStats.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 w-full">
      <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <FiAward className="text-green-500" />
        {t('awarenessTrainingStats.title')}
      </h2>

      {/* Metriche del percorso: i "corsi" sono i moduli di un unico percorso */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-800">{training.users_enrolled || 0}</div>
          <div className="text-sm text-gray-500">{t('awarenessTrainingStats.usersEnrolled')}</div>
        </div>
        <div className="text-center p-3 bg-emerald-50 rounded-lg" title={t('awarenessTrainingStats.fullCompletionHint')}>
          <div className="text-2xl font-bold text-emerald-600">{training.users_full_completion || 0}</div>
          <div className="text-sm text-gray-500">{t('awarenessTrainingStats.fullCompletionUsers')}</div>
        </div>
        <div className="text-center p-3 bg-indigo-50 rounded-lg">
          <div className="text-2xl font-bold text-indigo-600">{training.total_courses_available || 0}</div>
          <div className="text-sm text-gray-500">{t('awarenessTrainingStats.coursesAvailable')}</div>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{(training.completion_rate || 0).toFixed(1)}%</div>
          <div className="text-sm text-gray-500">{t('awarenessTrainingStats.completion')}</div>
        </div>
      </div>

      {/* Barra Progresso Completamento Globale */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">{t('awarenessTrainingStats.globalCompletionRate')}</span>
          <span className="text-sm font-bold text-gray-800">{(training.completion_rate || 0).toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-green-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${training.completion_rate || 0}%` }}
          ></div>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          {t('awarenessTrainingStats.coursesCompletedOf', { completed: training.courses_completed || 0, total: training.courses_target || 0, users: training.users_enrolled || 0, courses: training.total_courses_available || 0 })}
        </div>
      </div>

      {/* Tabella Breakdown per Modulo */}
      {training.course_breakdown && training.course_breakdown.length > 0 && (
        <div className="mb-2">
          <h3 className="text-md font-medium text-gray-700 mb-3">{t('awarenessTrainingStats.perCourse')}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.course')}</th>
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.enrolled')}</th>
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.completed')}</th>
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.avgProgress')}</th>
                </tr>
              </thead>
              <tbody>
                {training.course_breakdown.map((course, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-2 px-4 text-sm text-gray-800">
                      {course.course_name || t('awarenessTrainingStats.unknownCourse')}
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-600 text-center">{course.users_enrolled || 0}</td>
                    <td className="py-2 px-4 text-sm text-gray-600 text-center">{course.users_completed || 0}</td>
                    <td className="py-2 px-4 text-sm text-center">
                      {course.avg_progress !== undefined ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          course.avg_progress >= 80 ? 'bg-green-100 text-green-700' :
                          course.avg_progress >= 60 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {course.avg_progress.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
